import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface Props {
  templates: EbookTemplate[];
  onPreview: (t: EbookTemplate) => void;
  favoriteIds: string[];
  onToggleFavorite: (id: string) => void;
}

const RANK_COLORS = ['#f59e0b', '#94a3b8', '#cd7c2f', '#6366f1', '#10b981', '#ec4899'];
const RANK_LABELS = ['🥇', '🥈', '🥉', '#4', '#5', '#6'];

const MOCK_AUTHORS = [
  { name: 'Sarah Chen', avatar: 'https://public.readdy.ai/ai/img_res/f6c5eab12195c32e8b8b9d525016eccc.jpg' },
  { name: 'Marcus Webb', avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20headshot%20confident%20portrait%20clean%20background&width=80&height=80&seq=author-2&orientation=squarish' },
  { name: 'Priya Sharma', avatar: 'https://readdy.ai/api/search-image?query=professional%20indian%20woman%20headshot%20smiling%20portrait%20clean%20background&width=80&height=80&seq=author-3&orientation=squarish' },
  { name: 'James Rivera', avatar: 'https://readdy.ai/api/search-image?query=professional%20hispanic%20man%20headshot%20portrait%20clean%20background&width=80&height=80&seq=author-4&orientation=squarish' },
  { name: 'Aiko Tanaka', avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20woman%20headshot%20smiling%20portrait%20clean%20background&width=80&height=80&seq=author-5&orientation=squarish' },
  { name: 'David Okafor', avatar: 'https://readdy.ai/api/search-image?query=professional%20african%20man%20headshot%20confident%20portrait%20clean%20background&width=80&height=80&seq=author-6&orientation=squarish' },
];

const GROWTH_NUMBERS = ['+48%', '+31%', '+27%', '+22%', '+19%', '+15%'];

export default function PopularThisWeek({ templates, onPreview, favoriteIds, onToggleFavorite }: Props) {
  if (templates.length === 0) return null;

  const top6 = templates.slice(0, 6);

  return (
    <div className="bg-gradient-to-b from-slate-900 to-slate-800 py-14">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 border border-amber-500/30 rounded-full">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></div>
                <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Live Trending</span>
              </div>
            </div>
            <h2 className="text-2xl font-extrabold text-white">Popular This Week</h2>
            <p className="text-slate-400 text-sm mt-1">The most downloaded &amp; highest-rated eBook templates right now</p>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-400">
            <i className="ri-refresh-line text-base"></i>
            <span>Updated daily</span>
          </div>
        </div>

        {/* Top 3 Hero Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
          {top6.slice(0, 3).map((template, idx) => {
            const author = MOCK_AUTHORS[idx];
            const growth = GROWTH_NUMBERS[idx];
            const isFavorite = favoriteIds.includes(template.id);
            return (
              <div
                key={template.id}
                onClick={() => onPreview(template)}
                className="group relative bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                {/* Rank Badge */}
                <div className="absolute top-4 left-4 z-20">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-base font-extrabold shadow-lg"
                    style={{ backgroundColor: RANK_COLORS[idx] + '25', border: `2px solid ${RANK_COLORS[idx]}60` }}
                  >
                    <span>{RANK_LABELS[idx]}</span>
                  </div>
                </div>

                {/* Heart */}
                <button
                  onClick={e => { e.stopPropagation(); onToggleFavorite(template.id); }}
                  className={`absolute top-4 right-4 z-20 w-8 h-8 rounded-full flex items-center justify-center border transition-all cursor-pointer backdrop-blur-sm ${isFavorite ? 'bg-rose-500 border-rose-500 text-white' : 'bg-black/30 border-white/20 text-white/70 hover:text-rose-400'}`}
                >
                  <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                </button>

                {/* Cover Image */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={template.image}
                    alt={template.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
                  {/* Color accent strip */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: template.color }}></div>
                </div>

                {/* Card Body */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-sm leading-tight line-clamp-2 group-hover:text-amber-300 transition-colors">{template.name}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">{template.category}</p>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-xs font-bold text-white">{template.rating}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-download-line text-slate-400 text-xs"></i>
                      <span className="text-xs text-slate-300">{template.downloads.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <i className="ri-arrow-up-line text-emerald-400 text-xs"></i>
                      <span className="text-xs font-bold text-emerald-400">{growth}</span>
                      <span className="text-xs text-slate-500">this week</span>
                    </div>
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                    <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 border border-white/20">
                      <img src={author.avatar} alt={author.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-400 truncate">by <span className="text-white font-medium">{author.name}</span></p>
                    </div>
                    <button
                      className="text-xs font-bold px-3 py-1 rounded-lg text-white transition-all whitespace-nowrap"
                      style={{ backgroundColor: template.color + 'cc' }}
                    >
                      Use
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom 3 Compact List */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {top6.slice(3).map((template, idx) => {
            const rank = idx + 4;
            const author = MOCK_AUTHORS[idx + 3];
            const growth = GROWTH_NUMBERS[idx + 3];
            const isFavorite = favoriteIds.includes(template.id);
            return (
              <div
                key={template.id}
                onClick={() => onPreview(template)}
                className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer flex items-stretch gap-0"
              >
                {/* Thumbnail */}
                <div className="relative w-24 flex-shrink-0 overflow-hidden">
                  <img src={template.image} alt={template.name} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/40" />
                  <div className="absolute top-2 left-2 w-6 h-6 rounded-lg flex items-center justify-center text-xs font-extrabold text-white" style={{ backgroundColor: RANK_COLORS[idx + 3] + '99' }}>
                    #{rank}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: template.color }}></div>
                </div>

                {/* Info */}
                <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                  <div>
                    <h3 className="font-bold text-white text-xs leading-tight line-clamp-2 group-hover:text-amber-300 transition-colors mb-1">{template.name}</h3>
                    <p className="text-xs text-slate-500 line-clamp-1">{template.category}</p>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <span className="text-xs font-bold text-white">{template.rating}</span>
                      </div>
                      <span className="text-xs text-slate-500">{(template.downloads / 1000).toFixed(1)}k</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <i className="ri-arrow-up-line text-emerald-400 text-xs"></i>
                      <span className="text-xs font-bold text-emerald-400">{growth}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/10">
                    <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                      <img src={author.avatar} alt={author.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs text-slate-400 truncate">{author.name}</span>
                    <button
                      onClick={e => { e.stopPropagation(); onToggleFavorite(template.id); }}
                      className={`ml-auto w-6 h-6 rounded-full flex items-center justify-center border transition-all cursor-pointer ${isFavorite ? 'bg-rose-500 border-rose-500 text-white' : 'border-white/20 text-white/50 hover:text-rose-400'}`}
                    >
                      <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-xs`}></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">Discover what everyone is reading this week</p>
        </div>
      </div>
    </div>
  );
}
